import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type EmailValidation = {
  email: string;
  emailErrorText: string;
  emailValid: boolean;
};

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]{1,64}(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// eslint-disable-next-line no-control-regex
const invalidCharsRegex = /([À-ž]|[^\x00-\x7F]+)/;

const validateEmail = (
  email: string | undefined,
  t: (key: string) => string,
): EmailValidation => {
  const hasInvalidChars = email && invalidCharsRegex.exec(email);

  const isValidEmail = Boolean(
    email && emailRegex.exec(email) && !hasInvalidChars,
  );

  return {
    email: email ?? '',
    emailErrorText: hasInvalidChars ? t('Email Error Text') : '',
    emailValid: isValidEmail,
  };
};

export const useEmailValidation = (
  initialValue?: string,
): [EmailValidation, Dispatch<SetStateAction<string>>] => {
  const { t } = useTranslation();
  const [state, setState] = useState(validateEmail(initialValue, t));

  const setEmailValue = useCallback<Dispatch<SetStateAction<string>>>(
    (email) =>
      setState((s) => {
        if (typeof email === 'function') {
          email = email(s.email);
        }
        /* istanbul ignore next */
        return s.email === email ? s : validateEmail(email, t);
      }),
    [t],
  );

  return [state, setEmailValue];
};

export const useEmail = (email: string) => {
  const { t } = useTranslation();

  return validateEmail(email, t);
};
